import { useEffect, useContext, useRef, useState } from "react";
import PropTypes from "prop-types";

import { TYPES } from "../../../../config/constants/typesReducers";
import { DraftContext } from "../../context/DraftContext";
import RelativeParser from "../../../../ui/utils/RelativeParser";

import { Resizable } from "re-resizable";
import { IconButton, MenuItem, Select, Tooltip } from "@mui/material";
import { DeleteRounded } from "@mui/icons-material";
import Draggable from "react-draggable";
import { PageSizeContext } from "../../../../ui/context/page/PageSizeContext";

import { getIconFieldByType } from "../../../../ui/utils/fields";
import { DATE_FORMATS, DEFAULT_DATE_FORMAT } from "../../../../ui/utils/dateFormat";

export const DraftField = ({field}) => {
    const { pageSize } = useContext(PageSizeContext);
    const {draftState:{ conf:{ canEdit }, draft:{ signers } }, draftDispatch} = useContext(DraftContext);

    const fieldResizeRef = useRef();
    const fieldColor = useRef(signers[field.signerId].color);

    const [ toggleActivateField, setToggleActivateField ] = useState(false);
    const dateSelectOpenRef = useRef(false);
    const [ sizeField, setSizeField ] = useState(() => {
        const {width: relativeWidth, height: relativeHeight} = field.position.size;
        const {pageWidth, pageHeight} = pageSize;

        const {x: width, y: height} = RelativeParser.percentageToPixels(relativeWidth, relativeHeight, pageWidth, pageHeight);

        return {width, height};
    });  

    //Detecta el fin del arrastre del campo y actualiza las coordenadas en el reducer
    const onStop = (e, data) => {
        console.log('Detenido -> ', e, data);
        const {x, y} = data;
        const {id, position:{page, coords}} = field;
        const {pageWidth, pageHeight} = pageSize;

        const relativeCoords = RelativeParser.pixelsToPercentage(x, y, pageWidth, pageHeight);

        const action = {
            type: TYPES.DRAFT.MOVE_FIELD,
            payload: {
                fieldId: id,
                page,
                coords: {
                    x: (coords.x + relativeCoords.x), 
                    y: (coords.y + relativeCoords.y)
                },
            }
        }
        draftDispatch(action);
    };

    //Elimina el campo del arreglo de campos de la pagina y actualiza el viewer
    const handleRemoveField = () => {
        const {id, position:{page}} = field;

        const action = {
            type: TYPES.DRAFT.REMOVE_FIELD,
            payload: {
                fieldId: id,
                page
            }
        }
        draftDispatch(action);
    };

    //Cambia el formato de fecha del campo de tipo date
    const handleChangeDateFormat = (event) => {
        event.stopPropagation();
        const { id, position: { page } } = field;
        draftDispatch({
            type: TYPES.DRAFT.EDIT_FIELD_DATE_FORMAT,
            payload: { fieldId: id, page, dateFormat: event.target.value }
        });
    };

    //Actualiza el viewer cuando cambia el tamaño del campo
    const onResize = (event, direction, element) => setSizeField({width: element.offsetWidth, height: element.offsetHeight});

    //Actualiza el viewer cuando se deja de arrastrar el campo
    const onResizeStop = (event, direction, element) => {  
        const {id, position:{page}} = field;
        const { offsetWidth, offsetHeight } = element;
        const {pageWidth, pageHeight} = pageSize;
    
        setSizeField({ width: offsetWidth, height: offsetHeight });
        
        // Calcula el nuevo tamaño relativo usando el contenedor actual
        const relativeSizeField = RelativeParser.pixelsToPercentage(offsetWidth, offsetHeight, pageWidth, pageHeight);
        const {x: relativeWidth, y: relativeHeight} = relativeSizeField;

        const action = {
            type: TYPES.DRAFT.RESIZE_FIELD,
            payload: {
                fieldId: id,
                page,
                size: {
                    width: `${relativeWidth}%`,
                    height: `${relativeHeight}%`
                }
            }
        };
        draftDispatch(action);
    };

    //Activa el campo para que se pueda editar
    const onMouseDownField = (event) => {
        if (!canEdit) return;
        const {target} = event;

        if (
            toggleActivateField && 
            (target.classList.contains('fields__item-field--options') 
            || target.classList.contains('fields__item-field--options--item'))
        ) return;
        setToggleActivateField(true);
    }

    //Desactiva el campo para que no se pueda editar
    useEffect(()=>{
        if (!canEdit) return;

        const handleMouseDown = (event) => {
            if (dateSelectOpenRef.current) return;
            if (event.target.id == field.id) return;
            if (event.target.closest?.('.fields__item-field--options')) return;
            if (event.target.classList.contains('fields__item-field--resize')) return;
            setToggleActivateField(false);
        }

        window.addEventListener('click', handleMouseDown);
        return () => {
            window.removeEventListener('click', handleMouseDown);
        }
    },[field.id, canEdit]);

    // Calcula el tamaño del campo en función del tamaño del contenedor
    useEffect(()=>{
        const {pageWidth, pageHeight} = pageSize;
        const {width: relativeWidth, height: relativeHeight} = field.position.size;

        const sizeField = RelativeParser.percentageToPixels(relativeWidth, relativeHeight, pageWidth, pageHeight);
        const {x: width, y: height} = sizeField;

        setSizeField({width, height});
    },[field.position.size, pageSize]);

    const enableResizable = {
        top: false,
        right: false,
        bottom: false,
        left: false,
        topRight: toggleActivateField,
        bottomLeft: toggleActivateField,
        topLeft: toggleActivateField,
        bottomRight: toggleActivateField,
    }

    const handleClassesResizable = {
        topRight: "fields__item-field--resize fields__item-field--resize-tr ", 
        topLeft: "fields__item-field--resize fields__item-field--resize-tl", 
        bottomRight: "fields__item-field--resize fields__item-field--resize-br", 
        bottomLeft: "fields__item-field--resize fields__item-field--resize-bl"
    }

    const handleStylesResizable = {
        topRight:    { width: 12, height: 12, right: 2,  top: 2,    zIndex: 200, cursor: 'ne-resize',  display: toggleActivateField ? 'block' : 'none' },
        topLeft:     { width: 12, height: 12, left: 2,   top: 2,    zIndex: 200, cursor: 'nw-resize',  display: toggleActivateField ? 'block' : 'none' },
        bottomRight: { width: 12, height: 12, right: 2,  bottom: 2, zIndex: 200, cursor: 'se-resize',  display: toggleActivateField ? 'block' : 'none' },
        bottomLeft:  { width: 12, height: 12, left: 2,   bottom: 2, zIndex: 200, cursor: 'sw-resize',  display: toggleActivateField ? 'block' : 'none' },
    }
    
    return (
            <Draggable 
                disabled={!canEdit}
                cancel=".fields__item-field--resize, .fields__item-field--options"
                handle=".fields__item-field"
                onStop={onStop}
                position={{x: 0, y: 0}}
                scale={1}
                bounds="parent">
                <Resizable
                    ref={fieldResizeRef}
                    bounds={"parent"}
                    minWidth='5%'
                    minHeight='3%'
                    maxWidth='50%'
                    maxHeight='50%'
                    size={sizeField}
                    onResizeStop={onResizeStop}
                    onResize={onResize}
                    boundsByDirection={true}
                    enable={enableResizable}
                    handleClasses={handleClassesResizable}
                    handleStyles={handleStylesResizable}
                    className = {`fields__item-field ${canEdit && 'enabled' }`}
                    style={{
                        opacity: (canEdit && toggleActivateField) ? '1':'0.9',
                        position: 'absolute',
                        backgroundColor: fieldColor.current.background,
                        color: fieldColor.current.contrast,

                        left: `${field.position.coords.x}%`,
                        top: `${field.position.coords.y}%`
                    }}>
                    <div
                        onMouseDown={onMouseDownField}
                        id={field.id}
                        className="fields__item-field--content">
                        { getIconFieldByType(field.type) }
                            {
                                sizeField.height > 60 &&
                                <div className="fields__item-field--signer sm-text" style={{color: fieldColor.current.contrast}}>
                                    {
                                        sizeField.height > 100 
                                        ? signers[field.signerId].name === ''
                                            ? <p> {signers[field.signerId].email} </p>
                                            : <>
                                                <strong>{signers[field.signerId].name}</strong>
                                                <p>{signers[field.signerId].email}</p>
                                            </>
                                        : signers[field.signerId].name === ''
                                            ? <p> {signers[field.signerId].email}</p>
                                            : <strong> {signers[field.signerId].name} </strong>
                                    }
                                </div>
                            }
                        <>
                            {
                                (canEdit && toggleActivateField) &&
                                <div className="fields__item-field--options">
                                        {field.type === 'date' && (
                                            <Tooltip title="Formato de fecha" placement="top" arrow>
                                                <Select
                                                    value={field.dateFormat || DEFAULT_DATE_FORMAT}
                                                    onChange={handleChangeDateFormat}
                                                    onOpen={() => { dateSelectOpenRef.current = true; }}
                                                    onClose={() => { setTimeout(() => { dateSelectOpenRef.current = false; }, 150); }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    size="small"
                                                    variant="standard"
                                                    className="fields__item-field--options--item"
                                                    sx={{
                                                        fontSize: '0.6rem',
                                                        minWidth: 90,
                                                        color: 'inherit',
                                                        '&:before': { borderColor: 'transparent' },
                                                        '& .MuiSelect-icon': { color: 'inherit', fontSize: 14 },
                                                    }}
                                                    disableUnderline>
                                                    {DATE_FORMATS.map(({ value, label }) => (
                                                        <MenuItem key={value} value={value} sx={{ fontSize: '0.78rem' }}>
                                                            {value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Tooltip>
                                        )}
                                        <IconButton
                                            onClick={handleRemoveField}
                                            className="fields__item-field--options--item"
                                            size="small">
                                            <DeleteRounded style={{fontSize: 18}}/>
                                        </IconButton>
                                </div>
                            }
                        </>
                    </div>
                </Resizable>
            </Draggable>
    );
}

DraftField.propTypes = {
    field: PropTypes.object.isRequired,
    //pageRef: PropTypes.object.isRequired,
    pageSize: PropTypes.object.isRequired,
};